<template>
  <div class="navigation" @focusout="handleDropdownFocusout">
    <span
      class="visually-hidden"
      v-text="$string('general.site_header.navigation')"
    />

    <nav class="navigation__nav container">
      <ul class="navigation__list">
        <template v-for="tier1 of desktop.items">
          <li
            v-if="isMenuItemAccessibleByMarket(tier1.markets)"
            :key="tier1.handle"
            class="navigation__item"
            :class="{ [$class('active')]: activeDropdown === tier1.handle }"
          >
            <!--
              eslint-disable vuejs-accessibility/no-static-element-interactions,
              vuejs-accessibility/mouse-events-have-key-events
            -->
            <span
              class="navigation__link-wrapper"
              :data-handle="tier1.handle"
              @mouseenter="handleDropdownHover"
              @focus="handleDropdownHover"
            >
              <!--
                eslint-enable vuejs-accessibility/no-static-element-interactions,
                vuejs-accessibility/mouse-events-have-key-events
              -->
              <component
                :is="tier1.url.includes('#ignore') ? 'p' : 'a'"
                :href="tier1.url.includes('#ignore') ? null : tier1.url"
                class="navigation__link text-desktop-paragraph-xs-bold"
                @click="handleTrackingLinkClick(tier1)"
                v-text="tier1.title"
              />

              <button
                v-if="tier1.children"
                class="navigation__dropdown-trigger"
                type="button"
                @keydown.enter.space="handleDropdownTriggerKeydown(tier1)"
              >
                <span
                  class="visually-hidden"
                  v-text="
                    `
                  ${$string('accessibility.open')} ${tier1.title}
                `
                  "
                />

                <icon-chevron-down />
              </button>
            </span>

            <nav
              v-if="tier1.children"
              class="navigation__dropdown"
              js-navigation="dropdown"
            >
              <div class="navigation__dropdown-container container">
                <template v-if="tier1.children.type === 'thumbnails'">
                  <p
                    class="navigation__dropdown-title text-desktop-headlines-h6"
                    v-text="
                      $string('general.site_header.dropdown_title', {
                        replace: { title: tier1.title },
                      })
                    "
                  />

                  <ul class="navigation__dropdown-thumbnails">
                    <template v-for="(item, index) of tier1.children.menu">
                      <li
                        v-if="isMenuItemAccessibleByMarket(item.markets)"
                        :key="`thumbnail-${index}-${item.url}`"
                        class="navigation__dropdown-item"
                      >
                        <a
                          v-if="item.image"
                          class="navigation__dropdown-link navigation__dropdown-link--image"
                          :href="item.url"
                          @click="
                            handleTrackingLinkClick(item, `${tier1.title} > `)
                          "
                        >
                          <div class="navigation__dropdown-thumbnail">
                            <responsive-image
                              :image="item.image"
                              :padding="false"
                              ratio="1:1"
                              sizes="150px"
                              widths="150, 300, 450"
                            />
                          </div>

                          <span
                            class="navigation__dropdown-label"
                            v-text="item.title"
                          />
                        </a>
                      </li>
                    </template>
                  </ul>

                  <div class="text-right">
                    <a
                      class="navigation__dropdown-view-all"
                      :href="tier1.url"
                      @click="handleTrackingLinkClick(tier1)"
                      v-text="
                        $string('general.site_header.view_all', {
                          replace: {
                            title: tier1.title,
                          },
                        })
                      "
                    />
                  </div>
                </template>

                <template v-else>
                  <div class="navigation__dropdown-item-wrapper">
                    <div>
                      <p
                        class="navigation__dropdown-title text-desktop-headlines-h6"
                        v-text="
                          $string('general.site_header.dropdown_title', {
                            replace: { title: tier1.title },
                          })
                        "
                      />

                      <ul class="navigation__dropdown-item-list">
                        <template v-for="item of tier1.children.menu">
                          <li
                            v-if="isMenuItemAccessibleByMarket(item.markets)"
                            :key="item.title"
                          >
                            <a
                              class="navigation__dropdown-link text-desktop-paragraph-s-regular"
                              :href="item.url"
                              @click="
                                handleTrackingLinkClick(
                                  item,
                                  `${tier1.title} > `,
                                )
                              "
                              v-text="item.title"
                            />
                          </li>
                        </template>
                      </ul>
                    </div>

                    <div v-if="tier1.children.images">
                      <div class="navigation__dropdown-images">
                        <template
                          v-for="(teaser, index) of tier1.children.images"
                          :key="`${teaser.link}-${index}`"
                        >
                          <menu-teaser
                            desktop
                            :is-viewed="hoveredDropdown === tier1.handle"
                            position="header-navigation"
                            :teaser="teaser"
                          />
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </nav>
          </li>
        </template>
      </ul>
    </nav>
  </div>
</template>

<script>
/**
 * Vue: Navigation (navigation)
 * -----------------------------------------------------------------------------
 * Desktop navigation links.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 *
 */

import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';
import ResponsiveImage from '~global/images/responsive-image';
import MenuTeaser from '~async/menu-drawer/menu-teaser';
import { isAccessibleOnMarket } from '~/helpers/general';

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Navigation',

  components: {
    IconChevronDown,
    ResponsiveImage,
    MenuTeaser,
  },

  props: {
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
  },

  data() {
    return {
      activeDropdown: null,
      hoveredDropdown: null,
    };
  },

  methods: {
    /**
     * Checks if the menu item is accessible by the current market.
     */
    isMenuItemAccessibleByMarket(markets) {
      return isAccessibleOnMarket(markets, 'thumbnail');
    },

    /**
     * Handle dropdown focusout for accessibility.
     */
    handleDropdownFocusout(event) {
      const dropdown = event.target.closest('[js-navigation="dropdown"]');

      if (!dropdown) {
        return;
      }

      const focusedElement = dropdown.querySelector(':focus');

      if (!focusedElement) {
        this.activeDropdown = null;
      }
    },

    /**
     * Handle hover for dropdown.
     */
    handleDropdownHover(event) {
      if (
        !event.target.classList.contains('navigation__link-wrapper') &&
        event.type !== 'focus'
      ) {
        return;
      }

      this.hoveredDropdown = event.target.dataset.handle;
    },

    /**
     * Handle dropdown keydown for accessibility.
     * @param {Object} tier - Dropdown tier object.
     */
    handleDropdownTriggerKeydown(tier) {
      this.activeDropdown = tier.handle;

      this.handleTrackingLinkClick(tier);
    },

    /**
     * Handle link click for dataLayer.
     * @param {Object} item - Dropdown item.
     * @param {String} parentBreadcrumbs - Parent breadcrumbs.
     */
    handleTrackingLinkClick(item, parentBreadcrumbs = '') {
      cnvs.EventBus.emit('dataLayer:navigation_select', {
        event: 'navigation_select',
        click_item: `${parentBreadcrumbs}${item.title}`,
        nav_element: 'header',
      });
    },
  },
};
</script>
